import type { SampleOrderDetailDto } from "@hisense/shared";
import { describe, vnDate } from "@hisense/shared";
import { printHtml } from "./printWindow";

const esc = (s: string) =>
  s.replace(/[&<>"]/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[ch]!);

const printDate = (ms: number | null | undefined) => {
  if (!ms) return "N/A";
  return new Date(ms).toLocaleDateString("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
};

const absUrl = (url: string | null | undefined): string | null => {
  const v = (url ?? "").trim();
  if (!v) return null;
  if (/^(https?:|data:|blob:)/i.test(v)) return v;
  const origin = window.location.origin;
  return `${origin}${v.startsWith("/") ? "" : "/"}${v}`;
};

const dash = (v: string | number | null | undefined) => {
  if (v == null || v === "") return "-";
  return esc(String(v));
};

/**
 * Sample-order Development Sheet print — layout matches generateCompletePrintHTML
 * in the legacy SampleOrder.js (A4 portrait, bilingual product cards, control
 * sample image, lab-test box). Control-sample images stay same-origin; we do
 * not proxy through a third-party image CDN.
 */
export function buildSampleOrderHtml(so: SampleOrderDetailDto) {
  const logoSrc = `${window.location.origin}/brand/hisense-logo.webp`;
  const party =
    so.orderType === "To Customer" ? (so.customerName ?? "") : (so.supplierName ?? "");
  const generated = new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

  const productBlocks = so.items.length
    ? so.items
        .map((it, index) => {
          /**
           * In ĐÚNG NHỮNG ẢNH DÒNG ĐÓ ĐANG CÓ.
           *
           * Xưởng cầm tờ này để so mẫu mới với mẫu khách gửi. Có cả hai thì in
           * hai tấm. Chỉ có ảnh sản phẩm thì in một tấm ảnh sản phẩm — trước
           * 14/09/2026 chỗ này in "No Image Available", giấu mất tấm ảnh đang
           * có sẵn; khách báo là lỗi. Không có tấm nào thì mới giữ chữ cũ.
           */
          const control = absUrl(it.controlSampleUrl);
          /**
           * Chỉ in ảnh sản phẩm như một tấm RIÊNG khi nó khác ảnh bản gốc.
           *
           * Form có nút "Dùng ảnh sản phẩm" chép thẳng ảnh sản phẩm sang ô bản
           * gốc, và dữ liệu thật đang dùng nó rất nhiều (316/453 dòng có hai ô
           * trỏ cùng một tệp). In cả hai khi chúng là một tấm thì tờ giấy có
           * hai ảnh giống hệt nhau — rối hơn chứ không rõ hơn.
           */
          const sameFile = it.controlSampleKey && it.imageKey
            ? it.controlSampleKey === it.imageKey
            : it.controlSampleUrl === it.imageUrl;
          const product = sameFile ? "" : absUrl(it.imageUrl);
          const photo = (src: string, caption: string) => `<div style="flex:1;min-width:0;">
                 <div style="font-size:8px;color:#555;margin-bottom:3px;">${esc(caption)}</div>
                 <img src="${esc(src)}" alt=""
                   style="max-width:100%;max-height:150px;object-fit:contain;border:1px solid #ccc;"
                   onerror="this.style.display='none';this.nextElementSibling.style.display='block';" />
                 <div style="display:none;padding:15px;border:1px dashed #ccc;color:#666;font-style:italic;font-size:9px;">
                   Image failed to load
                 </div>
               </div>`;
          const imgHtml = control || product
            ? `<div style="display:flex;gap:6px;align-items:flex-start;">
                 ${product ? photo(product, "Product / 产品") : ""}
                 ${control ? photo(control, "Control Sample / 原版") : ""}
               </div>`
            : `<div style="padding:40px;border:1px dashed #ccc;color:#666;font-style:italic;font-size:9px;">
                 No Image Available
               </div>`;
          return `<table style="border:2px solid #000;margin-bottom:10px;page-break-inside:avoid;">
            <tr>
              <td colspan="2" style="background:#2563eb;color:#fff;font-weight:bold;padding:6px;font-size:11px;">
                Product ${index + 1}: ${esc(it.productNo || "-")} - ${esc(describe(it, so.printLanguage) || "No description")}
              </td>
            </tr>
            <tr>
              <td style="padding:10px;vertical-align:top;width:65%;">
                <table style="width:100%;border:none;">
                  <tr>
                    <td style="border:none;padding:3px 6px;width:50%;font-size:9px;">
                      <strong>Product ID / 产品编号:</strong><br>
                      <span class="mono b">${dash(it.productNo)}</span>
                    </td>
                    <td style="border:none;padding:3px 6px;width:50%;font-size:9px;">
                      <strong>Sub Ref / 子参考:</strong><br>${dash(it.subRef)}
                    </td>
                  </tr>
                  <tr>
                    <td style="border:none;padding:3px 6px;font-size:9px;">
                      <strong>Type / 类型:</strong><br>${dash(it.type)}
                    </td>
                    <td style="border:none;padding:3px 6px;font-size:9px;">
                      <strong>Brand / 品牌:</strong><br>${dash(it.brand)}
                    </td>
                  </tr>
                  <tr>
                    <td colspan="2" style="border:none;padding:3px 6px;font-size:9px;">
                      <strong>Description / 描述:</strong><br>${dash(describe(it, so.printLanguage))}
                    </td>
                  </tr>
                  <tr>
                    <td style="border:none;padding:3px 6px;font-size:9px;">
                      <strong>Color / 颜色:</strong><br>${dash(it.color)}
                    </td>
                    <td style="border:none;padding:3px 6px;font-size:9px;">
                      <strong>Size / 尺寸:</strong><br>${dash(it.size)}
                    </td>
                  </tr>
                  <tr>
                    <td style="border:none;padding:3px 6px;font-size:9px;white-space:pre-wrap;">
                      <strong>Remarks / 备注:</strong><br>${dash(it.remarks)}
                    </td>
                    <td style="border:none;padding:3px 6px;font-size:9px;">
                      <strong>Request Qty / 要求数量:</strong><br>${dash(it.requestQuantity)}
                    </td>
                  </tr>
                  <tr>
                    <td colspan="2" style="border:none;padding:3px 6px;font-size:9px;">
                      <strong>Retained Qty / 保留数量:</strong><br>${dash(it.retainedQuantity)}
                    </td>
                  </tr>
                </table>
                <div class="description-box" style="margin-top:8px;">
                  <strong style="font-size:9px;">Control Sample Notes / 原版备注:</strong><br>
                  <span style="font-size:9px;">${esc(it.controlSampleDescription || "No notes provided")}</span>
                </div>
              </td>
              <td style="padding:10px;vertical-align:top;width:35%;border-left:2px solid #ddd;">
                <div style="text-align:center;">
                  <strong style="display:block;margin-bottom:5px;font-size:10px;color:#2563eb;">${control && product ? "Product &amp; Control Sample / 产品与原版" : control ? "Control Sample / 原版" : product ? "Product / 产品" : "Control Sample / 原版"}</strong>
                  ${imgHtml}
                </div>
              </td>
            </tr>
          </table>`;
        })
        .join("")
    : `<p style="text-align:center;padding:20px;color:#666;">No products added</p>`;

  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>${esc(so.sampleOrderNumber || "Sample-Order")}-Development-Sheet</title>
  <style>
    @page { margin: 8mm; size: A4; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    * { box-sizing: border-box; }
    body {
      font-family: Arial, sans-serif; margin: 0; padding: 10px; color: #000;
      line-height: 1.2; font-size: 10px; background: white;
      -webkit-print-color-adjust: exact; print-color-adjust: exact;
    }
    .mono { font-family: "Courier New", monospace; }
    .b { font-weight: bold; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 8px; }
    th, td { border: 1px solid #000; padding: 4px 6px; text-align: left; vertical-align: top; }
    .header-section {
      display: flex; justify-content: space-between; align-items: center;
      margin-bottom: 10px; border-bottom: 2px solid #000; padding-bottom: 8px;
    }
    .description-box {
      font-size: 9px; line-height: 1.2; padding: 4px;
      background: #f9f9f9; border: 1px solid #ddd;
    }
    .section-header {
      margin: 8px 0 4px; font-size: 11px; font-weight: bold; color: #2563eb;
    }
    .content-box {
      padding: 6px; border: 2px solid #000; min-height: 30px;
      background: #f9f9f9; font-size: 10px; line-height: 1.3;
    }
    .footer-section {
      text-align: center; margin-top: 10px; padding-top: 6px;
      border-top: 1px solid #000; color: #000; font-size: 8px;
    }
    @media print {
      body { margin: 0; padding: 8px; }
      * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
    }
  </style>
</head>
<body>
  <div class="header-section">
    <div>
      <img src="${esc(logoSrc)}" alt="Hisense Button Co., Ltd"
        style="max-height:50px;max-width:200px;object-fit:contain;"
        onerror="this.style.display='none';this.nextElementSibling.style.display='block';" />
      <div style="display:none;padding:8px;border:2px solid #000;max-width:250px;">
        <strong>Hisense Button Co., Ltd</strong>
      </div>
    </div>
    <div style="text-align:right;">
      <h1 style="margin:0;color:#2563eb;font-size:20px;font-weight:bold;">SAMPLE ORDER</h1>
      <h2 style="margin:3px 0;color:#2563eb;font-size:13px;font-weight:bold;">Development Sheet</h2>
      <p style="margin:3px 0 0;font-size:11px;font-family:'Courier New',monospace;">
        <strong>Order #: ${esc(so.sampleOrderNumber || "N/A")}</strong>
      </p>
      <p style="margin:2px 0 0;font-size:10px;">Status: <strong>${esc(so.status || "N/A")}</strong></p>
    </div>
  </div>

  <table style="border:2px solid #000;">
    <tr>
      <td style="background:#f0f0f0;font-weight:bold;width:15%;">Sample PO No.</td>
      <td style="width:20%;">${esc(so.sampleOrderNumber || "")}</td>
      <td style="background:#f0f0f0;font-weight:bold;width:10%;">To:</td>
      <td style="width:25%;">${esc(party)}</td>
      <td style="background:#f0f0f0;font-weight:bold;width:10%;">Date:</td>
      <td style="width:20%;">${printDate(so.orderDate)}</td>
    </tr>
  </table>

  <table style="border:2px solid #000;">
    <tr>
      <td style="background:#f0f0f0;font-weight:bold;width:20%;">Delivery Date:</td>
      <td style="width:30%;">${printDate(so.deliveryDate)}</td>
      <td style="background:#f0f0f0;font-weight:bold;width:20%;">Team/Department:</td>
      <td style="width:30%;">${esc(so.teamDepartment || "")}</td>
    </tr>
  </table>

  <table style="border:2px solid #000;">
    <tr>
      <td style="background:#f0f0f0;font-weight:bold;width:15%;">Contact Person:</td>
      <td style="width:35%;">${esc(so.contactPerson || "")}</td>
      <td style="background:#f0f0f0;font-weight:bold;width:15%;">Phone:</td>
      <td style="width:35%;">${esc(so.contactPhone || "")}</td>
    </tr>
    ${so.contactEmail ? `<tr>
      <td style="background:#f0f0f0;font-weight:bold;">Email:</td>
      <td colspan="3">${esc(so.contactEmail)}</td>
    </tr>` : ""}
  </table>

  <div style="margin:10px 0;">
    <h3 style="margin:0 0 5px;color:#2563eb;font-size:12px;font-weight:bold;">Product Control Samples / 产品原版:</h3>
    ${productBlocks}
  </div>

  <!--
    Khối "Lab Test Request" đã bỏ theo yêu cầu khách 13/09/2026; ghi chú đơn hàng
    thay vào đúng vị trí đó.

    LUÔN in khối này kể cả khi trống, không in có điều kiện như trước: tờ giấy
    giữ nguyên hình dạng giữa các đơn, nên người cầm tờ in biết ô ghi chú bỏ
    trống chứ không phải hệ thống nuốt mất nội dung.

    Xuống dòng người dùng gõ được giữ nguyên bằng white-space:pre-wrap — họ
    nói sẽ nhập nhiều, mà gộp hết thành một khối chữ liền thì không đọc được.
  -->
  <!--
    Thông tin gửi hàng thay chỗ khối "Lab Test Request" cũ. Ba trường tách
    riêng chứ không phải một ô chữ: số vận đơn là thứ người nhận tờ này tra
    nhiều nhất, phải nhìn ra ngay chứ không lẫn trong một đoạn ghi chú.
  -->
  <div style="margin:10px 0;">
    <h3 class="section-header">Courier Info / 快递信息:</h3>
    <table style="width:100%;border-collapse:collapse;font-size:10px;">
      <tr>
        <td style="width:22%;padding:4px 6px;"><strong>Courier / 快递公司:</strong></td>
        <td style="width:28%;padding:4px 6px;">${dash(so.courier)}</td>
        <td style="width:22%;padding:4px 6px;"><strong>Tracking No. / 运单号:</strong></td>
        <td style="padding:4px 6px;font-family:monospace;">${dash(so.trackingNo)}</td>
      </tr>
      <tr>
        <td style="padding:4px 6px;"><strong>Ship Date / 寄出日期:</strong></td>
        <td colspan="3" style="padding:4px 6px;">${so.shippedAt ? esc(vnDate(so.shippedAt)) : "-"}</td>
      </tr>
    </table>
  </div>

  <!--
    Ghi chú GIỮ NGUYÊN là ghi chú, in ngay dưới. Khách nói "đổi remarks thành
    courier info", nhưng 22 đơn mẫu đang có ghi chú SẢN XUẤT chứ không phải
    thông tin vận chuyển — đổi tên là dán nhãn sai lên chúng.
  -->
  <div style="margin:10px 0;">
    <h3 class="section-header">Remarks / 备注:</h3>
    <div class="content-box" style="white-space:pre-wrap;">${esc(so.remarks || "-")}</div>
  </div>

  <div class="footer-section">
    <p style="margin:0;">Document generated on ${esc(generated)}</p>
  </div>
</body>
</html>`;

  return html;
}

export function printSampleOrder(so: SampleOrderDetailDto) {printHtml(buildSampleOrderHtml(so));}
